import styled from '@emotion/styled';
import { Skeleton } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient';
import { ButtonActivityIndicator } from '@trinity/chat-components/components/presentation/Button.ActivityIndicator';
import { MessageTestingProgressBar } from './ProgressBar';
import { MessageTestingResultsTabs } from './ResultsTabs';
import { useExportMessageTestingPpt } from '@trinity/chat-components/hooks/useExportMessageTestingPpt';
import { useMessageTestingRun } from '@trinity/chat-components/hooks/useMessageTestingRun';
import type { SegmentOption, TestableMessage } from './types';

interface MessageTestingResultsPanelProps {
  runId: number;
  /** When provided, renders a close (×) button in the header. */
  onClose?: () => void;
  /** Optional subtitle rendered below the panel title (e.g. a back link). */
  headerSubtitle?: React.ReactNode;
  /** Extra action buttons rendered in the header next to Download PPT. */
  extraHeaderActions?: React.ReactNode;
  /** Called when the user clicks the segmentation run link. Receives the runKey for navigation. */
  onSegmentationRunClick?: (runKey: string) => void;
}

export function MessageTestingResultsPanel({
  runId,
  onClose,
  headerSubtitle,
  extraHeaderActions,
  onSegmentationRunClick,
}: MessageTestingResultsPanelProps): React.JSX.Element {
  const apiClient = useChatApiClient();

  const { data: runInputs, isLoading: isLoadingInputs } = useQuery({
    queryKey: ['message-testing-run-inputs', runId],
    queryFn: () => apiClient.messageTesting.getRun(runId),
  });

  const messages: TestableMessage[] = runInputs?.messages ?? [];
  const segments: SegmentOption[] = runInputs?.segments ?? [];
  const brand = runInputs?.brand;
  const objective = runInputs?.objective;
  const segRun = runInputs?.segmentationRun ?? null;

  const { status, progress, cells, cellErrors, error, rollups } = useMessageTestingRun(runId);
  const { exporting, exportPpt } = useExportMessageTestingPpt(runId);

  if (isLoadingInputs) {
    return (
      <Root>
        <PanelHeader>
          <PanelTitleGroup>
            <PanelTitle>Simulated Message Testing Results</PanelTitle>
            {headerSubtitle}
          </PanelTitleGroup>
          <HeaderActions>{onClose && <CloseButton onClick={onClose}>&times;</CloseButton>}</HeaderActions>
        </PanelHeader>
        <LoadingContainer>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="rectangular" width="100%" height={200} />
        </LoadingContainer>
      </Root>
    );
  }

  return (
    <Root>
      <PanelHeader>
        <PanelTitleGroup>
          <PanelTitle>Simulated Message Testing Results</PanelTitle>
          {headerSubtitle}
        </PanelTitleGroup>
        <HeaderActions>
          {(status === 'analyzing' || status === 'completed') && (
            <ButtonActivityIndicator
              color="primary"
              variant="brick"
              size="small"
              loading={exporting}
              onClick={exportPpt}
            >
              Download PPT
            </ButtonActivityIndicator>
          )}
          {extraHeaderActions}
          {onClose && <CloseButton onClick={onClose}>&times;</CloseButton>}
        </HeaderActions>
      </PanelHeader>

      <MessageTestingProgressBar status={status} progress={progress} error={error} />

      {segRun && (
        <RunMeta>
          <RunMetaItem>
            <strong>Segmentation Run:</strong> {segRun.projectTitle || segRun.runKey}
            {onSegmentationRunClick && (
              <>
                {' '}
                <SegRunLink onClick={() => onSegmentationRunClick(segRun.runKey)}>
                  (View Dashboard)
                </SegRunLink>
              </>
            )}
          </RunMetaItem>
        </RunMeta>
      )}

      {(brand || objective) && (
        <RunMeta>
          {brand && (
            <RunMetaItem>
              <strong>Brand:</strong> {brand}
            </RunMetaItem>
          )}
          {objective && (
            <RunMetaItem>
              <strong>Objective:</strong> {objective}
            </RunMetaItem>
          )}
        </RunMeta>
      )}

      {(cells.length > 0 || messages.length > 0) && (
        <MessageTestingResultsTabs
          messages={messages}
          segments={segments}
          cells={cells}
          cellErrors={cellErrors}
          rollups={rollups}
        />
      )}
    </Root>
  );
}

// ─── Panel-specific styled components ───────────────────────────────────────

const Root = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 6px 2px rgba(60, 64, 67, 0.15);
  height: 100%;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
`;

const PanelTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PanelTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  color: #1a1a1a;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  padding: 0 4px;

  &:hover {
    color: #1a1a1a;
  }
`;

const RunMeta = styled.div`
  padding: 10px 16px;
  display: flex;
  gap: 20px;
  font-size: 13px;
  color: #333;
  background: #f5f7fa;
  border-bottom: 1px solid #e0e0e0;
`;

const RunMetaItem = styled.span`
  strong {
    color: #555;
    margin-right: 4px;
  }
`;

const SegRunLink = styled.span`
  color: #6366f1;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 16px;
`;
